import type { CSSProperties } from "react";

/**
 * Marketing image wrapper that emits <picture> with AVIF + WebP
 * sources and a PNG fallback. Source files are pre-generated by
 * scripts/optimise-images.mjs at the widths declared in TARGETS
 * there; this component just stitches the URL pattern.
 *
 * Owen 2026-05-28 (Ship 9 / image audit).
 *
 * Naming convention: `foo.png` → `foo-{w}w.avif` and `foo-{w}w.webp`
 * siblings in the same directory.
 */

type OptImgProps = {
  /** Absolute path to the PNG fallback, e.g. "/foreman-ui/foreman-logo.png" */
  src: string;
  /** [1x, 2x] widths emitted by optimise-images.mjs */
  widths: [number, number];
  alt: string;
  className?: string;
  /** Intrinsic display dimensions — prevents CLS while AVIF arrives. */
  width?: number;
  height?: number;
  /** "high" on the LCP-eligible above-the-fold image, default elsewhere. */
  fetchPriority?: "high" | "low" | "auto";
  /** "eager" for above-the-fold, "lazy" everywhere else. Default lazy. */
  loading?: "eager" | "lazy";
  /** Async decoding is fine for nearly everything. */
  decoding?: "async" | "sync" | "auto";
  style?: CSSProperties;
};

export function OptImg({
  src,
  widths,
  alt,
  className,
  width,
  height,
  fetchPriority,
  loading = "lazy",
  decoding = "async",
  style,
}: OptImgProps) {
  const ext = src.match(/\.(png|jpe?g)$/i)?.[1];
  if (!ext) {
    // Source without a known raster extension — fall back to plain img.
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        style={style}
      />
    );
  }
  const base = src.slice(0, -1 - ext.length);
  const [w1, w2] = widths;
  return (
    <picture>
      <source
        type="image/avif"
        srcSet={`${base}-${w1}w.avif 1x, ${base}-${w2}w.avif 2x`}
      />
      <source
        type="image/webp"
        srcSet={`${base}-${w1}w.webp 1x, ${base}-${w2}w.webp 2x`}
      />
      <img
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        style={style}
      />
    </picture>
  );
}
